import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const OccupancyHeatmap = ({ data, onRoomClick }) => {
  const [selectedBuilding, setSelectedBuilding] = useState(data?.[0]?.building || '');
  const [hoveredRoom, setHoveredRoom] = useState(null);

  // Status-based color logic (matches user spec):
  // Available  = blue/grey (bg-muted / slate)
  // Reserved   = yellow (amber)
  // Occupied   = red (error/rose)
  // Maintenance = orange
  const getRoomColor = (room) => {
    const status = room?.status;
    if (status === 'Occupied')     return 'bg-rose-500 hover:bg-rose-600';
    if (status === 'Reserved')     return 'bg-amber-400 hover:bg-amber-500';
    if (status === 'Maintenance')  return 'bg-orange-500 hover:bg-orange-600';
    // Available (default)
    return 'bg-slate-300 dark:bg-slate-600 hover:bg-slate-400 dark:hover:bg-slate-500';
  };

  const getTextColor = (room) => {
    const status = room?.status;
    if (status === 'Occupied')    return 'text-white';
    if (status === 'Reserved')    return 'text-amber-900';
    if (status === 'Maintenance') return 'text-white';
    return 'text-slate-600 dark:text-slate-200';
  };

  // Update selectedBuilding when data changes (e.g. after filter)
  const buildingList = data?.map(b => b?.building) || [];
  const effectiveBuilding = buildingList?.includes(selectedBuilding)
    ? selectedBuilding
    : buildingList?.[0] || '';

  const selectedBuildingData = data?.find(b => b?.building === effectiveBuilding);

  // Per-building summary counts
  const buildingSummary = data?.map(b => {
    const total    = b?.floors?.reduce((s, f) => s + f?.total, 0);
    const occupied = b?.floors?.reduce((s, f) => s + f?.occupied, 0);
    const reserved = b?.floors?.reduce(
      (s, f) => s + f?.rooms?.filter(r => r?.status === 'Reserved')?.length, 0
    );
    return { building: b?.building, total, occupied, reserved };
  });

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg md:text-xl font-semibold text-foreground">Occupancy Heatmap</h3>
          <p className="text-sm text-muted-foreground mt-1">Real-time room status by floor</p>
        </div>

        {/* Building selector with mini stats */}
        <div className="flex flex-wrap items-center gap-2">
          {buildingSummary?.map((b) => (
            <button
              key={b?.building}
              onClick={() => setSelectedBuilding(b?.building)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                effectiveBuilding === b?.building
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-muted text-foreground hover:bg-muted/80'
              }`}
            >
              <span>{b?.building}</span>
              {b?.occupied > 0 && (
                <span className="ml-1.5 text-xs opacity-75">({b?.occupied} occ)</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Floor grid */}
      <div className="space-y-4">
        {selectedBuildingData?.floors?.map((floor) => {
          const reservedCount = floor?.rooms?.filter(r => r?.status === 'Reserved')?.length;
          return (
            <div key={floor?.floor} className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Icon name="Building2" size={16} className="text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">Floor {floor?.floor}</span>
                <span className="text-xs text-muted-foreground">
                  {floor?.occupied} occupied
                  {reservedCount > 0 && `, ${reservedCount} reserved`}
                  {` / ${floor?.total} total`}
                </span>
              </div>

              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
                {floor?.rooms?.map((room) => (
                  <div
                    key={room?.roomNumber}
                    className="relative group"
                    onMouseEnter={() => setHoveredRoom(room)}
                    onMouseLeave={() => setHoveredRoom(null)}
                    onClick={() => onRoomClick && onRoomClick(room)}
                  >
                    <div
                      className={`aspect-square rounded-md ${getRoomColor(room)} cursor-pointer transition-all duration-150 shadow-sm flex items-center justify-center`}
                    >
                      <span className={`text-xs font-semibold ${getTextColor(room)}`}>
                        {room?.roomNumber}
                      </span>
                    </div>

                    {/* Tooltip */}
                    {hoveredRoom?.roomNumber === room?.roomNumber && hoveredRoom?.fullRoomNumber === room?.fullRoomNumber && (
                      <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-52 bg-popover border border-border rounded-lg shadow-xl p-3 pointer-events-none">
                        <p className="text-sm font-semibold text-foreground mb-2">
                          Room {room?.fullRoomNumber || room?.roomNumber}
                        </p>
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center gap-1.5">
                            <span
                              className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                                room?.status === 'Occupied'    ? 'bg-rose-500' :
                                room?.status === 'Reserved'   ? 'bg-amber-400' :
                                room?.status === 'Maintenance'? 'bg-orange-500' : 'bg-slate-400'
                              }`}
                            />
                            <span className="text-foreground font-medium">{room?.status}</span>
                          </div>
                          <p className="text-muted-foreground">
                            Type: <span className="text-foreground font-medium">{room?.type}</span>
                          </p>
                          {room?.resident && (
                            <p className="text-muted-foreground">
                              Resident: <span className="text-foreground font-medium">{room?.resident}</span>
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {(!selectedBuildingData?.floors || selectedBuildingData?.floors?.length === 0) && (
          <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
            <Icon name="Building2" size={18} className="mr-2" />
            No floor data available for this building.
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mt-6 pt-4 border-t border-border">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-slate-300 dark:bg-slate-600 border border-slate-400/30"></div>
          <span className="text-xs text-muted-foreground">Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-amber-400"></div>
          <span className="text-xs text-muted-foreground">Reserved</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-rose-500"></div>
          <span className="text-xs text-muted-foreground">Occupied</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-orange-500"></div>
          <span className="text-xs text-muted-foreground">Maintenance</span>
        </div>
      </div>
    </div>
  );
};

export default OccupancyHeatmap;